import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';
import { stepperInfo } from '../../forms/CreateRide/stepperInfo';
import { VEHICLE_TYPES } from '../../../constants/constants';
import { buildQuickScheduleDraft } from '../../../utils/buildQuickScheduleDraft';
import RiderDetailsPanel from './RiderDetailsPanel';
import TripDetailsPanel, { normalizeStepTwoBooleans } from './TripDetailsPanel';
import ScheduleRidePanel from './ScheduleRidePanel';
import {
  Button,
  Checkbox,
  Collapse,
  Input,
} from 'antd';
import { documentsReasons } from '../../forms/mock';
import { CloseOutlined } from '@ant-design/icons';
import infoIcon from '../../../assets/svgs/infoIcon.svg';
import downChevron from '../../../assets/svgs/downChevron.svg';
import styles from './quickScheduleNew.module.css';

const { Panel } = Collapse;
const { TextArea } = Input;

const PANEL_KEYS = [
  'rider-details',
  'trip-details',
  'schedule-ride',
  'driver-instructions',
  'expense-details',
];

const CollapseExpandIcon = ({ isActive }) => (
  <span className={styles.collapseExpandIcon} aria-hidden>
    <img
      src={downChevron}
      alt=""
      className={isActive ? styles.chevronOpen : styles.chevronClosed}
    />
  </span>
);

const FieldLabel = ({ children, required, bold }) => (
  <label
    className={`${styles.fieldLabel} ${bold ? styles.fieldLabelBold : ''} ${required ? styles.required : ''}`}
  >
    {children}
  </label>
);

const InfoAlert = ({ message }) => (
  <div className={styles.alertInfo}>
    <img src={infoIcon} alt="" className={styles.alertInfoIcon} aria-hidden />
    <span>{message}</span>
  </div>
);

const QuickScheduleNewFooter = ({
  isConfirmDisabled,
  onDetailView,
  onConfirm,
}) => (
  <div className={styles.footer}>
    <Button
      type="default"
      className={`${styles.footerBtn} ${styles.detailViewBtn}`}
      onClick={onDetailView}
    >
      Detailed View
    </Button>
    <Button
      type="default"
      className={`${styles.footerBtn} ${styles.confirmBtn}`}
      disabled={isConfirmDisabled}
      onClick={onConfirm}
    >
      Confirm
    </Button>
  </div>
);

const QuickScheduleNew = ({
  handleQuickSubmit,
  handleShowDetailView,
  syncFormikValues,
  showCovidField,
  showClinicSelect,
  healthOrganizations,
  onOrganizationChange,
  draft,
  handleSave,
  platformPreferences,
  rideOptions,
  defaultTimeZone,
  airportBufferTime,
  onFooterRender,
  onClose,
  costCenterField,
  showAdditionalSourceField,
  showDocumentReasonForRideField,
}) => {
  const user = useSelector((state) => state?.authorization?.user);
  const [stepOneValues, setStepOneValues] = useState(stepperInfo[0].values);
  const [stepOneValid, setStepOneValid] = useState(false);
  const [stepTwoValues, setStepTwoValues] = useState(() =>
    normalizeStepTwoBooleans(stepperInfo[1].values, stepperInfo[1].values.vehicleType),
  );
  const [stepTwoValid, setStepTwoValid] = useState(false);
  const [stepThreeValues, setStepThreeValues] = useState(stepperInfo[2].values);
  const [stepThreeValid, setStepThreeValid] = useState(false);
  const [stepFourValues, setStepFourValues] = useState(stepperInfo[3].values);
  const [stepFourValid, setStepFourValid] = useState(true);
  const [driverNotesLegA, setDriverNotesLegA] = useState('');
  const [driverNotesLegB, setDriverNotesLegB] = useState('');
  const [driverNotes, setDriverNotes] = useState('');
  const [seatValidation, setSeatValidation] = useState({
    needsCarSeat: false,
    totalSeats: 0,
    showSeatTypeValidationError: false,
  });

  const stepTwoSatisfied = useMemo(
    () =>
      stepTwoValues?.vehicleType === VEHICLE_TYPES.UBER_AMBULATORY_RIDE || stepTwoValid,
    [stepTwoValues?.vehicleType, stepTwoValid],
  );

  const hasSeatSelectionError = useMemo(() => {
    if (!stepTwoValues?.needsCarSeat) return false;
    const total =
      (stepTwoValues.carSeatByDriver || 0) +
      (stepTwoValues.carSeatByRider || 0) +
      (stepTwoValues.boosterSeatByDriver || 0) +
      (stepTwoValues.boosterSeatByRider || 0);
    return total === 0;
  }, [stepTwoValues]);

  const isRoundTrip = Boolean(stepThreeValues?.isRoundtrip);
  const isUberRide = stepTwoValues?.vehicleType === VEHICLE_TYPES.UBER_AMBULATORY_RIDE;
  const showRoundTripDriverNotes = isRoundTrip && isUberRide;

  const hasExpenseFields = useMemo(
    () =>
      Boolean(
        costCenterField?.show ||
          showAdditionalSourceField?.show ||
          showDocumentReasonForRideField?.show,
      ),
    [
      costCenterField?.show,
      showAdditionalSourceField?.show,
      showDocumentReasonForRideField?.show,
    ],
  );

  const [activeCollapseKeys, setActiveCollapseKeys] = useState(() =>
    PANEL_KEYS.filter((key) => key !== 'expense-details'),
  );

  useEffect(() => {
    if (hasExpenseFields) {
      setActiveCollapseKeys((prev) =>
        prev.includes('expense-details') ? prev : [...prev, 'expense-details'],
      );
    } else {
      setActiveCollapseKeys((prev) => prev.filter((key) => key !== 'expense-details'));
    }
  }, [hasExpenseFields]);

  const stepFourSchema = useMemo(
    () =>
      Yup.object().shape({
        rideNotes: Yup.string().nullable().optional().trim(),
        documentReasonForRide: showDocumentReasonForRideField?.show
          ? showDocumentReasonForRideField?.isRequired
            ? Yup.array().min(1).required()
            : Yup.array().default([])
          : Yup.array().default([]),
        otherReason: Yup.string()
          .nullable()
          .trim()
          .when('documentReasonForRide', {
            is: (vals) => Array.isArray(vals) && vals.includes('Other'),
            then: (schema) => schema.trim().required(),
            otherwise: (schema) => schema.nullable(),
          }),
        costCenter: costCenterField?.show
          ? costCenterField?.isRequired
            ? Yup.string().trim().required()
            : Yup.string().nullable()
          : Yup.string().nullable(),
        additionalFundingSource: showAdditionalSourceField?.show
          ? showAdditionalSourceField?.isRequired
            ? Yup.string().trim().required()
            : Yup.string().nullable()
          : Yup.string().nullable(),
      }),
    [costCenterField, showAdditionalSourceField, showDocumentReasonForRideField],
  );

  useEffect(() => {
    if (!hasExpenseFields) {
      setStepFourValid(true);
      return;
    }

    const valuesToValidate = {
      ...stepFourValues,
      documentReasonForRide: Array.isArray(stepFourValues?.documentReasonForRide)
        ? stepFourValues.documentReasonForRide
        : [],
    };

    stepFourSchema
      .validate(valuesToValidate, { abortEarly: false })
      .then(() => setStepFourValid(true))
      .catch(() => setStepFourValid(false));
  }, [stepFourValues, stepFourSchema, hasExpenseFields]);

  const mergedDraft = useMemo(
    () =>
      buildQuickScheduleDraft({
        step1: stepOneValues,
        step2: stepTwoValues,
        step3: stepThreeValues,
        step4: stepFourValues,
        user,
        existingDraft: draft,
        driverNotesLegA: showRoundTripDriverNotes ? driverNotesLegA : '',
        driverNotesLegB: showRoundTripDriverNotes ? driverNotesLegB : '',
        driverNotes: showRoundTripDriverNotes ? '' : driverNotes,
        isRoundTrip: showRoundTripDriverNotes,
      }),
    [
      stepOneValues,
      stepTwoValues,
      stepThreeValues,
      stepFourValues,
      user,
      draft,
      driverNotesLegA,
      driverNotesLegB,
      driverNotes,
      showRoundTripDriverNotes,
    ],
  );

  const buildCombined = useCallback(() => mergedDraft, [mergedDraft]);

  const syncAll = useCallback(
    (overrides = {}) => {
      const combined = { ...buildCombined(), ...overrides };
      syncFormikValues?.(combined);
      return combined;
    },
    [buildCombined, syncFormikValues],
  );

  const handleStepOneValues = useCallback(
    (values) => {
      setStepOneValues(values);
      syncAll({ step1: values });
    },
    [syncAll],
  );

  const handleStepTwoValues = useCallback(
    (values) => {
      setStepTwoValues(values);
      syncAll({ step2: values });
    },
    [syncAll],
  );

  const handleStepThreeValues = useCallback(
    (values) => {
      setStepThreeValues(values);
      syncAll({ step3: values });
    },
    [syncAll],
  );

  const handleStepFourChange = useCallback(
    (field, value) => {
      setStepFourValues((prev) => {
        const next = { ...prev, [field]: value };
        syncAll({ step4: next });
        return next;
      });
    },
    [syncAll],
  );

  const handleDocumentReasonToggle = useCallback(
    (reasonKey, checked) => {
      const prev = stepFourValues?.documentReasonForRide || [];
      const next = checked ? [...prev, reasonKey] : prev.filter((key) => key !== reasonKey);
      setStepFourValues((current) => {
        const updated = {
          ...current,
          documentReasonForRide: next,
          ...(reasonKey === 'Other' && !checked ? { otherReason: '' } : {}),
        };
        syncAll({ step4: updated });
        return updated;
      });
    },
    [stepFourValues?.documentReasonForRide, syncAll],
  );

  const handleDetailView = useCallback(() => {
    const combined = buildCombined();
    syncFormikValues?.(combined);
    handleShowDetailView?.(combined);
  }, [handleShowDetailView, buildCombined, syncFormikValues]);

  const handleConfirm = useCallback(() => {
    const combined = buildCombined();
    syncFormikValues?.(combined);
    handleQuickSubmit?.(combined, 'quickSchedule');
  }, [handleQuickSubmit, buildCombined, syncFormikValues]);

  useEffect(() => {
    if (!draft?.step4) return;
    const isDraftUberRoundTrip =
      draft.step3?.isRoundtrip &&
      draft.step2?.vehicleType === VEHICLE_TYPES.UBER_AMBULATORY_RIDE;
    setStepFourValues((prev) => {
      const next = { ...prev, ...draft.step4 };
      if (isDraftUberRoundTrip) {
        delete next.rideNotes;
      }
      return next;
    });
    if (isDraftUberRoundTrip) {
      setDriverNotesLegA(draft.step4?.driverNotesLegA || '');
      setDriverNotesLegB(draft.step4?.driverNotesLegB || '');
    } else if (draft.step4?.rideNotes) {
      setDriverNotes(draft.step4.rideNotes);
    }
  }, [draft?.id, draft?.step4, draft?.step3?.isRoundtrip, draft?.step2?.vehicleType]);

  const isConfirmDisabled = useMemo(
    () =>
      !stepOneValid ||
      !stepTwoSatisfied ||
      !stepThreeValid ||
      (hasExpenseFields && !stepFourValid) ||
      hasSeatSelectionError,
    [
      stepOneValid,
      stepTwoSatisfied,
      stepThreeValid,
      hasExpenseFields,
      stepFourValid,
      hasSeatSelectionError,
    ],
  );

  useEffect(() => {
    if (!onFooterRender) return undefined;

    onFooterRender(
      <QuickScheduleNewFooter
        isConfirmDisabled={isConfirmDisabled}
        onDetailView={handleDetailView}
        onConfirm={handleConfirm}
      />,
    );

    return () => onFooterRender(null);
  }, [
    onFooterRender,
    isConfirmDisabled,
    handleDetailView,
    handleConfirm,
  ]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.modalHeader}>
        <h1 className={styles.modalTitle}>Request a Ride</h1>
        <button
          type="button"
          className={styles.modalCloseBtn}
          onClick={onClose}
          aria-label="Close"
        >
          <CloseOutlined className={styles.modalCloseIcon} />
        </button>
      </div>

      <div className={styles.wrapperScroll}>
        <Collapse
        className={styles.collapse}
        activeKey={activeCollapseKeys}
        onChange={setActiveCollapseKeys}
        expandIcon={({ isActive }) => <CollapseExpandIcon isActive={isActive} />}
        expandIconPlacement="end"
        bordered={false}
      >
        <Panel header="Rider Details" key="rider-details">
          <div className={styles.panelBody}>
            <RiderDetailsPanel
              showCovidField={showCovidField}
              showClinicSelect={showClinicSelect}
              healthOrganizations={healthOrganizations}
              onOrganizationChange={onOrganizationChange}
              draft={mergedDraft}
              handleSave={handleSave}
              onValuesChange={handleStepOneValues}
              onValidChange={setStepOneValid}
            />
          </div>
        </Panel>

        <Panel header="Trip Details" key="trip-details">
          <div className={styles.panelBody}>
            <TripDetailsPanel
              draft={mergedDraft}
              stepOneValues={stepOneValues}
              rideOptions={rideOptions}
              minTimeFromRideRequestToPickup={platformPreferences?.minTimeFromRideRequestToPickup}
              isUberEnabled={draft?.isUberEnabled}
              onValuesChange={handleStepTwoValues}
              onValidChange={setStepTwoValid}
              onSeatValidationChange={setSeatValidation}
            />
          </div>
        </Panel>

        <Panel header="Schedule Ride" key="schedule-ride">
          <div className={styles.panelBody}>
            <ScheduleRidePanel
              draft={mergedDraft}
              stepOneValues={stepOneValues}
              stepTwoValues={stepTwoValues}
              platformPreferences={platformPreferences}
              defaultTimeZone={defaultTimeZone}
              airportBufferTime={airportBufferTime}
              onValuesChange={handleStepThreeValues}
              onValidChange={setStepThreeValid}
            />
          </div>
        </Panel>

        <Panel
          header={
            <span>
              Driver Instructions
              <span className={styles.optionalLabel}>(Optional)</span>
            </span>
          }
          key="driver-instructions"
        >
          <div className={styles.panelBody}>
            {showRoundTripDriverNotes ? (
              <>
                <div className={styles.formRowFull}>
                  <FieldLabel>Message To Driver Leg A</FieldLabel>
                  <TextArea
                    className={`${styles.inputFilled} ${styles.driverInstructionsTextArea}`}
                    placeholder="Write Message"
                    rows={1}
                    value={driverNotesLegA}
                    onChange={(e) => setDriverNotesLegA(e.target.value)}
                  />
                </div>
                <div className={styles.formRowFull}>
                  <FieldLabel>Message To Driver Leg B</FieldLabel>
                  <TextArea
                    className={`${styles.inputFilled} ${styles.driverInstructionsTextArea}`}
                    placeholder="Write Message"
                    rows={1}
                    value={driverNotesLegB}
                    onChange={(e) => setDriverNotesLegB(e.target.value)}
                  />
                </div>
              </>
            ) : (
              <div className={styles.formRowFull}>
                <FieldLabel>Message To Driver</FieldLabel>
                <TextArea
                  className={`${styles.inputFilled} ${styles.driverInstructionsTextArea}`}
                  placeholder="Write Message"
                  rows={1}
                  value={driverNotes}
                  onChange={(e) => setDriverNotes(e.target.value)}
                />
              </div>
            )}
          </div>
        </Panel>

        {hasExpenseFields && (
        <Panel header="Expense Details" key="expense-details">
          <div className={styles.panelBody}>
            {costCenterField?.show && (
              <div className={styles.formRowFull}>
                <FieldLabel required={costCenterField?.isRequired}>Cost Center</FieldLabel>
                <Input
                  className={styles.inputFilled}
                  placeholder="Cost Center"
                  value={stepFourValues?.costCenter || ''}
                  onChange={(e) => handleStepFourChange('costCenter', e.target.value)}
                />
              </div>
            )}
            {showAdditionalSourceField?.show && (
              <div className={styles.formRowFull}>
                <FieldLabel required={showAdditionalSourceField?.isRequired}>
                  Additional Funding Source
                </FieldLabel>
                <Input
                  className={styles.inputFilled}
                  placeholder="i.e Program, Payer, Grant Funding"
                  value={stepFourValues?.additionalFundingSource || ''}
                  onChange={(e) => handleStepFourChange('additionalFundingSource', e.target.value)}
                />
              </div>
            )}
            {showDocumentReasonForRideField?.show && (
              <div className={styles.formRowFull}>
                <FieldLabel required={showDocumentReasonForRideField?.isRequired} bold>
                  Document reason for the ride
                </FieldLabel>
                <div className={styles.documentReasonList}>
                  {Object.keys(documentsReasons).map((reasonKey) => (
                    <Checkbox
                      key={reasonKey}
                      checked={stepFourValues?.documentReasonForRide?.includes(reasonKey)}
                      onChange={(e) => handleDocumentReasonToggle(reasonKey, e.target.checked)}
                    >
                      {documentsReasons[reasonKey]}
                    </Checkbox>
                  ))}
                </div>
                {stepFourValues?.documentReasonForRide?.includes('Other') && (
                  <Input
                    className={styles.inputFilled}
                    placeholder="Other reason"
                    value={stepFourValues?.otherReason || ''}
                    onChange={(e) => handleStepFourChange('otherReason', e.target.value)}
                  />
                )}
              </div>
            )}
          </div>
        </Panel>
        )}
      </Collapse>
      </div>
    </div>
  );
};

export default QuickScheduleNew;
export { QuickScheduleNew };
